import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePageInput,
  ListPagesQuery,
  PageDetail,
  PageListResponse,
  UpdatePageInput,
} from '@news-cms/shared';
import { ApiError, api } from './api';
import { normalizeLegacyLayoutClient } from './page-layout-walk';
import { toast } from '@/components/ui/toaster';

function qs(filter: Partial<ListPagesQuery>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return params.toString();
}

/* ---------- query options ---------- */

export const pagesListQueryOptions = (filter: Partial<ListPagesQuery>) =>
  queryOptions({
    queryKey: ['pages', 'list', filter] as const,
    queryFn: () => api<PageListResponse>(`/api/admin/pages?${qs(filter)}`),
  });

export const pageDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['pages', 'detail', id] as const,
    queryFn: () => api<{ page: PageDetail }>(`/api/admin/pages/${id}`).then((d) => d.page),
  });

/* ---------- slug check ---------- */

export interface SlugCheckResult {
  valid: boolean;
  available: boolean;
  reason: 'empty' | 'invalid' | null;
}

export async function checkPageSlug(slug: string, excludeId?: string): Promise<SlugCheckResult> {
  const params = new URLSearchParams({ slug });
  if (excludeId) params.set('excludeId', excludeId);
  return api<SlugCheckResult>(`/api/admin/pages/_slug-check?${params.toString()}`);
}

/* ---------- mutations ---------- */

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['pages'] });
  // Homepage editor reads slug='home' via the homepage alias — keep its
  // cache fresh whenever any page mutates (in case the home page changed).
  void qc.invalidateQueries({ queryKey: ['homepage'] });
}

export function useCreatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePageInput) => {
      const data = await api<{ page: PageDetail }>('/api/admin/pages', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data.page;
    },
    onSuccess: (page) => {
      qc.setQueryData(pageDetailQueryOptions(page.id).queryKey, page);
      invalidateAll(qc);
      toast.success('สร้าง Page เรียบร้อย');
    },
    onError: (e) => toast.error(pageErrorMessage(e)),
  });
}

/** Orphan override row reported by PATCH /pages/:id when the saved layout
 *  drops a slot id that an active slot_override still references. */
export interface OrphanedOverride {
  id: string;
  slotId: string;
}

interface UpdatePageResult {
  page: PageDetail;
  orphanedOverrides: OrphanedOverride[];
}

export function useUpdatePage(id: string) {
  const qc = useQueryClient();
  return useMutation<UpdatePageResult, unknown, UpdatePageInput>({
    mutationFn: async (input: UpdatePageInput) => {
      // Normalize layout before save — legacy slots/carousels loaded from
      // DB may lack the new required fields (preset, itemIndex). Apply
      // defaults so the strict Zod schema on the backend accepts the
      // payload. Idempotent on already-normalized layouts.
      const normalized: UpdatePageInput = input.layout
        ? { ...input, layout: normalizeLegacyLayoutClient(input.layout) }
        : input;
      const data = await api<{
        page: PageDetail;
        orphanedOverrides?: OrphanedOverride[];
      }>(`/api/admin/pages/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(normalized),
      });
      return { page: data.page, orphanedOverrides: data.orphanedOverrides ?? [] };
    },
    onSuccess: (result) => {
      qc.setQueryData(pageDetailQueryOptions(result.page.id).queryKey, result.page);
      invalidateAll(qc);
      // Also refresh promos list — orphaned rows now show with red banner.
      void qc.invalidateQueries({ queryKey: ['slot-overrides'] });
      if (result.orphanedOverrides.length > 0) {
        toast.success(
          `บันทึก Page เรียบร้อย — มี ${result.orphanedOverrides.length} PR ที่ slot หาย (orphan)`,
        );
      } else {
        toast.success('บันทึก Page เรียบร้อย');
      }
    },
    onError: (e) => toast.error(pageErrorMessage(e)),
  });
}

export function useDeletePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ deleted: true }>(`/api/admin/pages/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['pages', 'detail', id] });
      invalidateAll(qc);
      toast.success('ลบ Page เรียบร้อย');
    },
    onError: (e) => toast.error(pageErrorMessage(e)),
  });
}

/* ---------- error messages ---------- */

export function pageErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'VALIDATION_ERROR': {
      // Surface Zod issue paths so operator can find the broken field
      // instead of staring at a generic message. Backend emits
      // `{ issues: ZodIssue[] }` in error.details.
      const details = e.details as { issues?: unknown } | undefined;
      const rawIssues = details?.issues;
      const issues =
        Array.isArray(rawIssues) && rawIssues.length > 0
          ? (rawIssues as Array<{ path: unknown[]; message: string }>)
          : null;
      if (!issues || issues.length === 0) return 'ข้อมูลไม่ถูกต้อง';
      const first = issues[0]!;
      const path = Array.isArray(first.path) ? first.path.join('.') : '';
      const more = issues.length > 1 ? ` (+${issues.length - 1} เพิ่มเติม)` : '';
      return `ข้อมูลไม่ถูกต้อง: ${path ? `${path} — ` : ''}${first.message}${more}`;
    }
    case 'SLUG_CONFLICT':
      return 'slug นี้ถูกใช้แล้ว';
    case 'RESERVED_SLUG':
      return 'slug "home" สงวนไว้สำหรับหน้าระบบ';
    case 'SYSTEM_PAGE_LOCKED':
      return 'หน้าระบบ — ลบหรือเปลี่ยน slug ไม่ได้';
    case 'NOT_FOUND':
      return 'ไม่พบ Page';
    case 'FORBIDDEN':
      return 'ไม่มีสิทธิ์ — ต้องมี settings.update';
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
